/**
 * Soft Petalz / Ajax Design Lab 2026 - Celebratory Confetti Animation Engine
 * High-performance 60fps canvas particle system for celebrating bundle completion & coupon application.
 */
(function() {
  'use strict';

  var activeCanvas = null;
  var activeContext = null;
  var activeParticles = [];
  var animationFrameId = null;
  var resizeHandler = null;

  var BRAND_COLORS = [
    '#8b9d7b', // Sage green primary
    '#6f8662', // Deep sage
    '#c9745d', // Terracotta accent
    '#e08269', // Warm coral
    '#d4a574', // Soft gold
    '#f5c26b', // Bright gold shimmer
    '#f5f3ef', // Warm cream
    '#2dd4bf', // Mint sparkle
    '#fb7185', // Rose petal
    '#ffffff'  // Pure sparkle white
  ];

  var SHAPES = ['rectangle', 'circle', 'star', 'ribbon'];

  function createParticle(originX, originY, options) {
    options = options || {};
    var shape = options.shape || SHAPES[Math.floor(Math.random() * SHAPES.length)];
    var colors = Array.isArray(options.colors) && options.colors.length > 0 ? options.colors : BRAND_COLORS;
    var color = colors[Math.floor(Math.random() * colors.length)];

    var minAngle = typeof options.minAngle === 'number' ? options.minAngle : -135;
    var maxAngle = typeof options.maxAngle === 'number' ? options.maxAngle : -45;
    var angleDeg = minAngle + Math.random() * (maxAngle - minAngle);
    var angleRad = (angleDeg * Math.PI) / 180;

    var minVelocity = options.minVelocity || 12;
    var maxVelocity = options.maxVelocity || 28;
    var velocity = minVelocity + Math.random() * (maxVelocity - minVelocity);

    var size = options.size || (shape === 'ribbon' ? (8 + Math.random() * 8) : (6 + Math.random() * 7));

    return {
      x: originX,
      y: originY,
      vx: Math.cos(angleRad) * velocity,
      vy: Math.sin(angleRad) * velocity,
      shape: shape,
      color: color,
      size: size,
      width: shape === 'ribbon' ? size * 0.4 : size,
      height: shape === 'ribbon' ? size * 2.2 : size,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 12,
      tilt: Math.random() * 360,
      tiltSpeed: (Math.random() - 0.5) * 14,
      wobble: Math.random() * 10,
      wobbleSpeed: 0.08 + Math.random() * 0.1,
      gravity: typeof options.gravity === 'number' ? options.gravity : 0.42,
      drag: typeof options.drag === 'number' ? options.drag : 0.965,
      opacity: 1,
      fadeSpeed: 0.006 + Math.random() * 0.008,
      age: 0,
      maxAge: 160 + Math.floor(Math.random() * 80)
    };
  }

  function ensureCanvas() {
    if (activeCanvas && document.body.contains(activeCanvas)) {
      return activeCanvas;
    }

    var canvas = document.createElement('canvas');
    canvas.className = 'theme-confetti-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999999';
    canvas.style.overflow = 'hidden';

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);

    activeCanvas = canvas;
    activeContext = canvas.getContext('2d');
    if (activeContext) {
      activeContext.scale(dpr, dpr);
    }

    document.body.appendChild(canvas);

    resizeHandler = function() {
      if (!activeCanvas || !activeContext) return;
      var curDpr = Math.min(window.devicePixelRatio || 1, 2);
      activeCanvas.width = Math.round(window.innerWidth * curDpr);
      activeCanvas.height = Math.round(window.innerHeight * curDpr);
      activeContext.setTransform(1, 0, 0, 1, 0, 0);
      activeContext.scale(curDpr, curDpr);
    };
    window.addEventListener('resize', resizeHandler, { passive: true });

    return activeCanvas;
  }

  function cleanupCanvas() {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }
    if (activeCanvas && activeCanvas.parentNode) {
      activeCanvas.parentNode.removeChild(activeCanvas);
    }
    activeCanvas = null;
    activeContext = null;
    activeParticles = [];
  }

  function drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
    var rot = (Math.PI / 2) * 3;
    var x = cx;
    var y = cy;
    var step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    for (var i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
  }

  function renderFrame() {
    if (!activeContext || !activeCanvas) {
      cleanupCanvas();
      return;
    }

    var width = window.innerWidth;
    var height = window.innerHeight;

    activeContext.clearRect(0, 0, width, height);

    var remaining = [];

    for (var i = 0; i < activeParticles.length; i++) {
      var p = activeParticles[i];
      p.age++;

      p.vx *= p.drag;
      p.vy = (p.vy * p.drag) + p.gravity;
      p.x += p.vx + Math.sin(p.wobble) * 0.8;
      p.y += p.vy;
      p.wobble += p.wobbleSpeed;
      p.rotation += p.rotationSpeed;
      p.tilt += p.tiltSpeed;

      if (p.age > p.maxAge - 40) {
        p.opacity = Math.max(0, p.opacity - p.fadeSpeed * 1.8);
      }

      if (p.opacity <= 0.01 || p.y > height + 80) {
        continue;
      }

      activeContext.save();
      activeContext.translate(p.x, p.y);
      activeContext.rotate((p.rotation * Math.PI) / 180);
      var tiltScaleX = Math.cos((p.tilt * Math.PI) / 180);
      activeContext.scale(tiltScaleX, 1);
      activeContext.globalAlpha = Math.max(0, Math.min(1, p.opacity));
      activeContext.fillStyle = p.color;

      if (p.shape === 'circle') {
        activeContext.beginPath();
        activeContext.arc(0, 0, p.size * 0.5, 0, Math.PI * 2);
        activeContext.fill();
      } else if (p.shape === 'star') {
        drawStar(activeContext, 0, 0, 4, p.size * 0.9, p.size * 0.35);
      } else if (p.shape === 'ribbon') {
        activeContext.fillRect(-p.width * 0.5, -p.height * 0.5, p.width, p.height);
      } else {
        // rectangle
        activeContext.fillRect(-p.size * 0.5, -p.size * 0.5, p.size, p.size * 0.7);
      }

      activeContext.restore();
      remaining.push(p);
    }

    activeParticles = remaining;

    if (activeParticles.length > 0) {
      animationFrameId = requestAnimationFrame(renderFrame);
    } else {
      cleanupCanvas();
    }
  }

  function spawnBurst(originX, originY, count, burstOptions) {
    ensureCanvas();
    burstOptions = burstOptions || {};
    var particlesToAdd = [];
    for (var i = 0; i < count; i++) {
      particlesToAdd.push(createParticle(originX, originY, burstOptions));
    }
    activeParticles = activeParticles.concat(particlesToAdd);

    if (!animationFrameId) {
      animationFrameId = requestAnimationFrame(renderFrame);
    }
  }

  /**
   * Main celebration trigger.
   * Emits celebratory confetti identical to Arata's reward effect.
   */
  function triggerConfetti(options) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    options = options || {};
    var style = options.style || 'celebration';
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;

    if (style === 'celebration' || style === 'bundle') {
      // Dual side cannons + center sparkle fountain (high impact celebration)
      // Left cannon
      spawnBurst(viewportWidth * 0.12, viewportHeight * 0.88, 55, {
        minAngle: -75,
        maxAngle: -25,
        minVelocity: 18,
        maxVelocity: 30,
        gravity: 0.44
      });

      // Right cannon
      window.setTimeout(function() {
        spawnBurst(viewportWidth * 0.88, viewportHeight * 0.88, 55, {
          minAngle: -155,
          maxAngle: -105,
          minVelocity: 18,
          maxVelocity: 30,
          gravity: 0.44
        });
      }, 120);

      // Center sparkle burst
      window.setTimeout(function() {
        spawnBurst(viewportWidth * 0.5, viewportHeight * 0.7, 45, {
          minAngle: -130,
          maxAngle: -50,
          minVelocity: 14,
          maxVelocity: 24,
          gravity: 0.38
        });
      }, 240);

    } else if (style === 'coupon' || style === 'drawer') {
      // Coupon applied celebration (focused toward center-right where cart drawer / coupon input sits)
      var targetX = options.x || (viewportWidth > 768 ? viewportWidth * 0.75 : viewportWidth * 0.5);
      var targetY = options.y || viewportHeight * 0.55;

      // Primary burst from coupon area
      spawnBurst(targetX, targetY, 65, {
        minAngle: -140,
        maxAngle: -40,
        minVelocity: 15,
        maxVelocity: 26,
        gravity: 0.42
      });

      // Supporting left side cannon for full festive feel
      window.setTimeout(function() {
        spawnBurst(viewportWidth * 0.2, viewportHeight * 0.85, 40, {
          minAngle: -70,
          maxAngle: -30,
          minVelocity: 16,
          maxVelocity: 26,
          gravity: 0.42
        });
      }, 100);

      // Supporting right cannon
      window.setTimeout(function() {
        spawnBurst(viewportWidth * 0.85, viewportHeight * 0.85, 45, {
          minAngle: -150,
          maxAngle: -110,
          minVelocity: 16,
          maxVelocity: 26,
          gravity: 0.42
        });
      }, 180);

    } else {
      // Point-based burst
      var originX = typeof options.x === 'number' ? options.x : viewportWidth * 0.5;
      var originY = typeof options.y === 'number' ? options.y : viewportHeight * 0.5;
      var count = options.count || 60;
      spawnBurst(originX, originY, count, options);
    }

    try {
      window.dispatchEvent(new CustomEvent('theme:confetti:fired', { detail: options }));
    } catch (e) {}
  }

  // Expose global API
  window.triggerThemeConfetti = triggerConfetti;
  window.themeConfetti = {
    trigger: triggerConfetti,
    burst: spawnBurst,
    cleanup: cleanupCanvas
  };

  // Listen for custom events
  window.addEventListener('theme:confetti', function(event) {
    var detail = (event && event.detail) || {};
    triggerConfetti(detail);
  });

  window.addEventListener('theme:coupon-applied', function(event) {
    var detail = (event && event.detail) || {};
    triggerConfetti(Object.assign({ style: 'coupon' }, detail));
  });

  window.addEventListener('theme:bundle-completed', function(event) {
    var detail = (event && event.detail) || {};
    triggerConfetti(Object.assign({ style: 'celebration' }, detail));
  });

})();
